// TelcoTrack — rezervace / přiřazení terénní práce (Fronta práce + Navolávání + mapa).

import { ROLES, expandLegacyRoles, hasObjectContentMutatorRole } from './roles.constants';

/**
 * Druhy práce (Fronta + Navolávání + mapa) — samostatná rezervace na objekt:
 * budovy (závrt, odbočka, VR, dig. cesta, záfuk, svár) + rozvaděče (postavení, záfuk, provaření).
 * Enum `zafuk` = produktové „záfuk budovy“ (bez DB rename).
 */
export const WORK_RESERVATION_KINDS = [
  'zavrt',
  'odboceni',
  'vr',
  'digitalni_cesta',
  'zafuk',
  'svar_budovy',
  'rozvadec_postaveni',
  'rozvadec_zafuk',
  'rozvadec_provareni',
] as const;
export type WorkReservationKind = (typeof WORK_RESERVATION_KINDS)[number];

/**
 * Druhy v hubu Navolávání (volání / kalendář nad budovami).
 * Pořadí = sloupce hub tabulky + topbar na detailu stavby.
 * Rozvaděčové kindy jsou Fronta-only (Navolávání zatím nemá ROZVADEC picker).
 */
export const NAVOLAVANI_WORK_KINDS = [
  'zavrt',
  'digitalni_cesta',
  'svar_budovy',
  'zafuk',
] as const;
export type NavolavaniWorkKind = (typeof NAVOLAVANI_WORK_KINDS)[number];

/** Krátké popisky hub / tabs Navolávání (Fronta drží delší `WORK_RESERVATION_KIND_LABELS_CS`). */
export const NAVOLAVANI_WORK_KIND_LABELS_CS: Record<NavolavaniWorkKind, string> = {
  zavrt: 'Závrt',
  digitalni_cesta: 'Digitální cesta',
  svar_budovy: 'Svár',
  zafuk: 'Záfuk',
};

export const WORK_RESERVATION_KIND_LABELS_CS: Record<WorkReservationKind, string> = {
  zavrt: 'Závrt',
  odboceni: 'Odbočka',
  vr: 'VR',
  digitalni_cesta: 'Digitální cesta',
  zafuk: 'Záfuk budovy',
  svar_budovy: 'Svár budovy',
  rozvadec_postaveni: 'Postavení rozvaděče',
  rozvadec_zafuk: 'Záfuk rozvaděče',
  rozvadec_provareni: 'Provaření rozvaděče',
};

/** Prefix Google / kalendářové události. */
export const WORK_RESERVATION_EVENT_PREFIX: Record<WorkReservationKind, string> = {
  zavrt: 'Závrt',
  odboceni: 'Odbočka',
  vr: 'VR',
  digitalni_cesta: 'Digitální cesta',
  zafuk: 'Záfuk budovy',
  svar_budovy: 'Svár budovy',
  rozvadec_postaveni: 'Postavení rozvaděče',
  rozvadec_zafuk: 'Záfuk rozvaděče',
  rozvadec_provareni: 'Provaření rozvaděče',
};

/** Object.type required for assign (Fronta rows). */
export type WorkReservationObjectType = 'BUDOVA' | 'ROZVADEC';

/**
 * Object types that can carry Fronta `work_reservations` and map mine/occupied highlight.
 * (Odbočka / VR / dig. cesta / … are work kinds on BUDOVA — ring paints on the BUDOVA.)
 */
export const WORK_RESERVATION_MAP_OBJECT_TYPES = ['BUDOVA', 'ROZVADEC'] as const;
export type WorkReservationMapObjectType = (typeof WORK_RESERVATION_MAP_OBJECT_TYPES)[number];

export function isWorkReservationMapObjectType(
  type: string | null | undefined,
): type is WorkReservationMapObjectType {
  const key = String(type ?? '')
    .trim()
    .toUpperCase();
  return (WORK_RESERVATION_MAP_OBJECT_TYPES as readonly string[]).includes(key);
}

/** Map marker ring colors (web CSS box-shadow + Android Mapbox icon halo). */
export const WORK_RESERVATION_MAP_HIGHLIGHT_COLORS = {
  /** Assigned to logged-in user, active reserved (actionable). */
  mine: '#16a34a',
  /** Assigned to someone else (overlap warning). */
  occupied: '#dc2626',
} as const;

export const WORK_RESERVATION_KIND_OBJECT_TYPE: Record<WorkReservationKind, WorkReservationObjectType> =
  {
    zavrt: 'BUDOVA',
    odboceni: 'BUDOVA',
    vr: 'BUDOVA',
    digitalni_cesta: 'BUDOVA',
    zafuk: 'BUDOVA',
    svar_budovy: 'BUDOVA',
    rozvadec_postaveni: 'ROZVADEC',
    rozvadec_zafuk: 'ROZVADEC',
    rozvadec_provareni: 'ROZVADEC',
  };

export function workReservationObjectTypeForKind(
  kind: WorkReservationKind,
): WorkReservationObjectType {
  return WORK_RESERVATION_KIND_OBJECT_TYPE[kind];
}

export function isNavolavaniWorkKind(value: string | null | undefined): value is NavolavaniWorkKind {
  const key = (value ?? '').trim().toLowerCase();
  return (NAVOLAVANI_WORK_KINDS as readonly string[]).includes(key);
}

export const WORK_RESERVATION_STATUSES = ['reserved', 'cancelled', 'done'] as const;
export type WorkReservationStatus = (typeof WORK_RESERVATION_STATUSES)[number];

export const WORK_RESERVATION_STATUS_LABELS_CS: Record<WorkReservationStatus, string> = {
  reserved: 'Rezervováno',
  cancelled: 'Zrušeno',
  done: 'Hotovo',
};

const KIND_SET = new Set<string>(WORK_RESERVATION_KINDS);
const STATUS_SET = new Set<string>(WORK_RESERVATION_STATUSES);

export function isWorkReservationKind(value: string | null | undefined): value is WorkReservationKind {
  const key = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  return KIND_SET.has(key);
}

export function isWorkReservationStatus(value: string | null | undefined): value is WorkReservationStatus {
  return STATUS_SET.has((value ?? '').trim().toLowerCase());
}

export function parseWorkReservationKind(value: unknown): WorkReservationKind | null {
  const key = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  return isWorkReservationKind(key) ? key : null;
}

/** Fronta práce tab → work kind (jen přiřaditelné záložky). */
export const WORK_QUEUE_TAB_TO_RESERVATION_KIND: Record<string, WorkReservationKind> = {
  'k-zavrtu': 'zavrt',
  'k-odboceni': 'odboceni',
  vr: 'vr',
  'digitalni-cesta': 'digitalni_cesta',
  zafuk: 'zafuk',
  provareni: 'svar_budovy',
  'rozvadec-postaveni': 'rozvadec_postaveni',
  'rozvadec-zafuk': 'rozvadec_zafuk',
  'rozvadec-provareni': 'rozvadec_provareni',
};

export function workReservationKindForWorkQueueTab(
  tab: string | null | undefined,
): WorkReservationKind | null {
  const key = String(tab ?? '').trim();
  return WORK_QUEUE_TAB_TO_RESERVATION_KIND[key] ?? null;
}

export function workQueueTabForReservationKind(kind: WorkReservationKind): string {
  switch (kind) {
    case 'zavrt':
      return 'k-zavrtu';
    case 'odboceni':
      return 'k-odboceni';
    case 'vr':
      return 'vr';
    case 'digitalni_cesta':
      return 'digitalni-cesta';
    case 'zafuk':
      return 'zafuk';
    case 'svar_budovy':
      return 'provareni';
    case 'rozvadec_postaveni':
      return 'rozvadec-postaveni';
    case 'rozvadec_zafuk':
      return 'rozvadec-zafuk';
    case 'rozvadec_provareni':
      return 'rozvadec-provareni';
  }
}

/** Manažer (legacy → finance) + admin / superadmin smí přiřazovat. */
export function hasWorkAssignRole(roles: string[] | undefined | null): boolean {
  return expandLegacyRoles(roles).some(
    (role) => role === ROLES.FINANCE || role === ROLES.ADMIN || role === ROLES.SUPERADMIN,
  );
}

/**
 * Technik / zodpovědný v pickeru Navolávání (a Fronta přiřazení):
 * uživatelé s oprávněním editovat obsah na mapě — ne filtr podle Google kalendáře.
 */
export function hasWorkReservationAssigneeRole(roles: string[] | undefined | null): boolean {
  return hasObjectContentMutatorRole(roles);
}

export function workReservationEventTitle(kind: WorkReservationKind, address: string): string {
  const place = address.trim() || 'objekt';
  return `${WORK_RESERVATION_EVENT_PREFIX[kind]} · ${place}`;
}

/** In-app notifikace při přiřazení / zrušení terénní práce. */
export const WORK_ASSIGNMENT_NOTIFICATION_OBJECT_TYPE = 'WORK';
export const WORK_ASSIGNMENT_NOTIFICATION_PHASE_ASSIGNED = 'WORK_ASSIGNED';
export const WORK_ASSIGNMENT_NOTIFICATION_PHASE_CANCELLED = 'WORK_CANCELLED';

export type WorkAssignmentNotificationKind = 'assigned' | 'cancelled';

export function formatWorkAssignmentNotificationMessage(params: {
  kind: WorkAssignmentNotificationKind;
  workKind: WorkReservationKind;
  objectLabel?: string | null;
  startsAt?: string | null;
}): string {
  const workLabel = WORK_RESERVATION_KIND_LABELS_CS[params.workKind] ?? 'Práce';
  const headline =
    params.kind === 'assigned'
      ? `Byla vám přiřazena práce: ${workLabel}`
      : `Byla vám zrušena přiřazená práce: ${workLabel}`;
  const lines = [headline];
  const place = String(params.objectLabel ?? '').trim();
  if (place) lines.push(place);
  const startsAt = String(params.startsAt ?? '').trim();
  if (startsAt) {
    const date = new Date(startsAt);
    if (Number.isFinite(date.getTime())) {
      lines.push(
        `Termín: ${date.toLocaleString('cs-CZ', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}`,
      );
    }
  }
  return lines.join('\n');
}

export type WorkReservationDto = {
  id: string;
  /** Null = free calendar appointment (not tied to an in-app building / Fronta row). */
  objectId: string | null;
  workKind: WorkReservationKind;
  status: WorkReservationStatus;
  assigneeUserId: string;
  assigneeDisplayName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  note: string | null;
  /** Free-text place when `objectId` is null (Navolávání calendar create). */
  locationLabel: string | null;
  googleEventId: string | null;
  googleCalendarId: string | null;
  assignedBy: string | null;
  assignedAt: string | null;
  updatedAt: string | null;
};

/**
 * Shrnutí pro mapu / frontu / object info (aktivní `reserved` z bbox/list enrichment).
 * Includes display fields needed in object detail + map popup.
 */
export type WorkReservationSummary = {
  id: string;
  workKind: WorkReservationKind;
  status: WorkReservationStatus;
  assigneeUserId: string;
  assigneeDisplayName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  note: string | null;
  assignedBy: string | null;
  assignedAt: string | null;
  updatedAt: string | null;
};

export type WorkReservationOccupancy =
  | { kind: 'mine'; reservation: WorkReservationSummary }
  | { kind: 'occupied'; reservation: WorkReservationSummary }
  | { kind: 'free' };

export function resolveWorkReservationOccupancy(
  reservations: ReadonlyArray<WorkReservationSummary> | null | undefined,
  workKind: WorkReservationKind,
  currentUserId: string | null | undefined,
): WorkReservationOccupancy {
  const mineId = (currentUserId ?? '').trim();
  const kindRows = (reservations ?? []).filter((row) => row.workKind === workKind);
  if (!kindRows.length) return { kind: 'free' };
  const mineRow = mineId ? kindRows.find((row) => row.assigneeUserId === mineId) : undefined;
  if (mineRow) return { kind: 'mine', reservation: mineRow };
  return { kind: 'occupied', reservation: kindRows[0]! };
}

/**
 * Planned termín for map / Fronta labels (`startsAt`/`endsAt` ISO).
 * Returns null when no start is set.
 */
export function formatWorkReservationScheduleLabel(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string | null {
  const startRaw = (startsAt ?? '').trim();
  if (!startRaw) return null;
  const start = new Date(startRaw);
  if (!Number.isFinite(start.getTime())) return null;

  const dateFmt = new Intl.DateTimeFormat('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat('cs-CZ', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const datePart = dateFmt.format(start);
  const startTime = timeFmt.format(start);

  const endRaw = (endsAt ?? '').trim();
  if (endRaw) {
    const end = new Date(endRaw);
    if (Number.isFinite(end.getTime()) && end.getTime() > start.getTime()) {
      const sameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();
      if (sameDay) {
        return `${datePart} ${startTime}–${timeFmt.format(end)}`;
      }
      return `${datePart} ${startTime} – ${dateFmt.format(end)} ${timeFmt.format(end)}`;
    }
  }
  return `${datePart} ${startTime}`;
}

/** One label/value row for object detail / map popup reservation blocks. */
export type WorkReservationInfoRow = { label: string; value: string };

/**
 * Full reservation fields for object info (detail page + map popup).
 * Skips empty optional fields.
 */
export function formatWorkReservationInfoRows(
  row: WorkReservationSummary | WorkReservationDto,
): WorkReservationInfoRow[] {
  const kindLabel =
    WORK_RESERVATION_KIND_LABELS_CS[row.workKind as WorkReservationKind] ?? String(row.workKind);
  const statusKey = (row.status ?? 'reserved') as WorkReservationStatus;
  const statusLabel = WORK_RESERVATION_STATUS_LABELS_CS[statusKey] ?? String(row.status ?? 'reserved');
  const assignee = (row.assigneeDisplayName ?? '').trim() || row.assigneeUserId || '—';
  const schedule = formatWorkReservationScheduleLabel(row.startsAt, row.endsAt);
  const out: WorkReservationInfoRow[] = [
    { label: 'Druh práce', value: kindLabel },
    { label: 'Stav', value: statusLabel },
    { label: 'Přiřazeno', value: assignee },
  ];
  if (schedule) out.push({ label: 'Termín', value: schedule });
  const note = (row.note ?? '').trim();
  if (note) out.push({ label: 'Poznámka', value: note });
  const assignedBy = (row.assignedBy ?? '').trim();
  if (assignedBy) out.push({ label: 'Přiřadil', value: assignedBy });
  const assignedAt = formatWorkReservationScheduleLabel(row.assignedAt, null);
  if (assignedAt) out.push({ label: 'Přiřazeno dne', value: assignedAt });
  const updatedAt = formatWorkReservationScheduleLabel(row.updatedAt, null);
  if (updatedAt) out.push({ label: 'Aktualizováno', value: updatedAt });
  return out;
}

/** Compact multi-line text for Android popup / tooltips. */
export function formatWorkReservationInfoText(
  row: WorkReservationSummary | WorkReservationDto,
): string {
  return formatWorkReservationInfoRows(row)
    .map((r) => `${r.label}: ${r.value}`)
    .join('\n');
}

export type WorkReservationInfoBlock = {
  title: string;
  rows: WorkReservationInfoRow[];
};

/** One or more reservation blocks for object detail / map INFORMACE. */
export function formatWorkReservationInfoBlocks(
  reservations: ReadonlyArray<WorkReservationSummary | WorkReservationDto> | null | undefined,
): WorkReservationInfoBlock[] {
  const list = reservations ?? [];
  if (!list.length) return [];
  const sorted = [...list].sort((a, b) => {
    const la = WORK_RESERVATION_KIND_LABELS_CS[a.workKind as WorkReservationKind] ?? String(a.workKind);
    const lb = WORK_RESERVATION_KIND_LABELS_CS[b.workKind as WorkReservationKind] ?? String(b.workKind);
    return la.localeCompare(lb, 'cs');
  });
  if (sorted.length === 1) {
    return [{ title: 'Rezervace práce', rows: formatWorkReservationInfoRows(sorted[0]) }];
  }
  return sorted.map((row) => ({
    title: WORK_RESERVATION_KIND_LABELS_CS[row.workKind as WorkReservationKind] ?? 'Rezervace',
    rows: formatWorkReservationInfoRows(row),
  }));
}

/** Pick the first reservation that has a planned termín (prefer mine when ids match). */
export function pickWorkReservationForScheduleLabel(
  reservations: ReadonlyArray<WorkReservationSummary> | null | undefined,
  currentUserId?: string | null,
): WorkReservationSummary | null {
  const rows = reservations ?? [];
  if (!rows.length) return null;
  const mineId = (currentUserId ?? '').trim();
  if (mineId) {
    const mineWithSchedule = rows.find(
      (row) => row.assigneeUserId === mineId && formatWorkReservationScheduleLabel(row.startsAt, row.endsAt),
    );
    if (mineWithSchedule) return mineWithSchedule;
  }
  return (
    rows.find((row) => formatWorkReservationScheduleLabel(row.startsAt, row.endsAt)) ?? rows[0] ?? null
  );
}

/**
 * Mapa — libovolná aktivní (`reserved`) rezervace (ne per záložka fronty).
 *
 * Filter rules for highlight rows (API `activeOnly`):
 * - status = `reserved` (not cancelled, not done) → still actionable
 * - mine = assigneeUserId === logged-in user
 * - occupied = any other assignee on the same paint target
 *
 * Paint target: reserved object (BUDOVA including `odboceni`, or ROZVADEC).
 */
export type MapWorkReservationVisual =
  | { kind: 'none' }
  | { kind: 'mine'; tooltip: string | null }
  | { kind: 'occupied'; tooltip: string };

/**
 * Mine / occupied marker state for the map.
 * Always paints reservation state (automatic highlight — the removed "Moje práce" map filter
 * must not be required). `highlightMyWorkEnabled` is ignored; kept for call-site compatibility.
 * When a planned termín (`startsAt`) is set, it is included in the tooltip / label.
 * Prefer mine over occupied when both exist on the same object.
 */
export function resolveMapWorkReservationVisual(
  reservations: ReadonlyArray<WorkReservationSummary> | null | undefined,
  currentUserId: string | null | undefined,
  _highlightMyWorkEnabled: boolean = true,
): MapWorkReservationVisual {
  const rows = reservations ?? [];
  if (rows.length === 0) return { kind: 'none' };
  const mineId = (currentUserId ?? '').trim();
  const mineRow = mineId ? rows.find((row) => row.assigneeUserId === mineId) : undefined;
  if (mineRow) {
    const schedule = formatWorkReservationScheduleLabel(mineRow.startsAt, mineRow.endsAt);
    const name = (mineRow.assigneeDisplayName ?? '').trim();
    let tooltip: string | null;
    if (schedule && name) tooltip = `Termín · ${schedule} · ${name}`;
    else if (schedule) tooltip = `Termín · ${schedule}`;
    else if (name) tooltip = `Moje · ${name}`;
    else tooltip = 'Moje';
    return { kind: 'mine', tooltip };
  }
  const others = rows.filter((row) => !mineId || row.assigneeUserId !== mineId);
  if (others.length === 0) return { kind: 'none' };
  const hit = others[0];
  const names = [
    ...new Set(
      others.map((row) => (row.assigneeDisplayName ?? '').trim() || row.assigneeUserId || '—'),
    ),
  ].join(', ');
  const schedule = formatWorkReservationScheduleLabel(hit?.startsAt, hit?.endsAt);
  return {
    kind: 'occupied',
    tooltip: schedule ? `Obsazeno · ${names} · ${schedule}` : `Obsazeno · ${names}`,
  };
}

/** Comma-separated assignee display names for Fronta / labels. */
export function formatWorkReservationAssigneeNames(
  reservations: ReadonlyArray<WorkReservationSummary | WorkReservationDto> | null | undefined,
): string {
  const list = reservations ?? [];
  if (!list.length) return '—';
  const names = [
    ...new Set(
      list.map((row) => (row.assigneeDisplayName ?? '').trim() || row.assigneeUserId || '—'),
    ),
  ];
  return names.join(', ') || '—';
}
